import React from "react";

import type { MedVisitDetailResponse } from "../../../types/medchron";
import { VisitsTableGroupHeader } from "./VisitsTableGroupHeader";
import { VisitsTableRow } from "./VisitsTableRow";
import {
  ChronologyViewType,
  type VisitCitationClickHandler,
  type VisitDisplay,
  type VisitsFlatItem,
} from "./types";

export interface VisitsTableProps {
  flatItems: VisitsFlatItem[];
  viewType: ChronologyViewType;
  expandedGroups: Record<string, boolean>;
  setExpandedGroups: React.Dispatch<React.SetStateAction<Record<string, boolean>>>;
  getVisitDetails: (visit: VisitDisplay) => MedVisitDetailResponse[];
  fileMapping: Record<string, string>;
  onCitationClick: VisitCitationClickHandler;
  formatVisitType: (id: string) => string;
  formatDetailCategory: (category: string, isCollapsed?: boolean) => string;
  formatDate: (d: string | null) => string;
  highlightQuery: string;
  editMode?: boolean;
  onEditModeRowClick?: (visitId: string) => void;
}

const HEADER_CELL_CLASS =
  "sticky top-0 z-20 h-11 border-b border-neutral-200 bg-card px-4 text-left align-middle text-2xs font-semibold uppercase tracking-micro text-neutral-500";

export const VisitsTable: React.FC<VisitsTableProps> = ({
  flatItems,
  viewType,
  expandedGroups,
  setExpandedGroups,
  getVisitDetails,
  fileMapping,
  onCitationClick,
  formatVisitType,
  formatDetailCategory,
  formatDate,
  highlightQuery,
  editMode,
  onEditModeRowClick,
}) => (
  <div className="visits-table-container pt-2">
    <table className="w-full min-w-[900px] border-separate border-spacing-0 text-sm">
      <thead>
        <tr>
          <th scope="col" className={`sticky left-0 z-30 ${HEADER_CELL_CLASS}`}>
            Date
          </th>
          <th scope="col" className={HEADER_CELL_CLASS}>
            Visit/Provider
          </th>
          <th scope="col" className={HEADER_CELL_CLASS}>
            Details
          </th>
          <th scope="col" className={HEADER_CELL_CLASS}>
            Citations
          </th>
        </tr>
      </thead>
      <tbody>
        {flatItems.map((item) => {
          if (item.kind === "header") {
            if (viewType === ChronologyViewType.DATE) return null;
            return (
              <tr key={`header-${item.groupKey}`}>
                <td colSpan={4} className="sticky top-11 z-20 bg-card p-0">
                  <VisitsTableGroupHeader
                    groupKey={item.groupKey}
                    groupLabel={item.groupLabel}
                    visitCount={item.visitCount}
                    viewType={viewType}
                    formatVisitType={formatVisitType}
                    isExpanded={expandedGroups[item.groupKey] ?? false}
                    onToggle={() =>
                      setExpandedGroups((prev) => ({
                        ...prev,
                        [item.groupKey]: !prev[item.groupKey],
                      }))
                    }
                  />
                </td>
              </tr>
            );
          }
          const { visit } = item;
          return (
            <VisitsTableRow
              key={visit.visit_id}
              visit={visit}
              details={getVisitDetails(visit)}
              fileMapping={fileMapping}
              onCitationClick={onCitationClick}
              formatVisitType={formatVisitType}
              formatDetailCategory={formatDetailCategory}
              formatDate={formatDate}
              highlightQuery={highlightQuery}
              editMode={editMode}
              onEditModeRowClick={onEditModeRowClick}
            />
          );
        })}
      </tbody>
    </table>
  </div>
);
